import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IBaseOWClassDescription, IOWClassAttributeDescription, IOWEnumAttributeDescription, OWAttributeDesciption } from "@multiweb/types/contracts";
import { ObjectUtils } from "Global/Base/Utils/ObjectUtils";
import { CaseInsensitiveMap } from "../../Global/Base/DataTypes/CaseInsensitiveMap";
import { MultiAsyncLock } from "../../Global/Base/Promises/AsyncLock";
import { AttributeDescription, IOWClassDescription } from "./Cache/ClassDescriptionCache/Interfaces";
import { ModelDescriptionsManager } from "./Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { SerializeFuncs } from "./Serializers";

const modelLogger = LazyLoadingSectionLogger.create(LoggingSection.modelDescription);

export class OWClassDescription implements IOWClassDescription {
    /**
     * Zámky pro načítání jednotlivých vazeb aby více dotazů spouštělo vždy jen jedno načítání
     */
    private readonly _associationLoadingLocks = new MultiAsyncLock();

    private _className: string;

    public get className(): string {
        return this._className;
    }

    private _childClasses: readonly string[];

    public get childClasses(): readonly string[] {
        return this._childClasses;
    }

    private _relatedClasses: readonly string[];

    public get relatedClasses(): readonly string[] {
        return this._relatedClasses;
    }

    private _originalClassName: string;

    public get originalClassName(): string {
        return this._originalClassName;
    }

    private _isPersistent: boolean;

    public get isPersistent(): boolean {
        return this._isPersistent;
    }

    private _signingAllowed: boolean;

    public get signingAllowed(): boolean {
        return this._signingAllowed;
    }

    private _encryptingAllowed: boolean;

    public get encryptingAllowed(): boolean {
        return this._encryptingAllowed;
    }

    private _versioningAllowed: boolean;

    public get versioningAllowed(): boolean {
        return this._versioningAllowed;
    }

    private _supportsContext: boolean;

    public get supportsContext(): boolean {
        return this._supportsContext;
    }

    private _supportsHierarchy: boolean;

    public get supportsHierarchy(): boolean {
        return this._supportsHierarchy;
    }

    private _passwordMasterLink: string;

    public get passwordMasterLink(): string {
        return this._passwordMasterLink;
    }

    public get attributes(): readonly AttributeDescription[] {
        return [...this._attributes.values()];
    }

    private _attributes: CaseInsensitiveMap<AttributeDescription> = new CaseInsensitiveMap();
    private _bindingAttributes: CaseInsensitiveMap<IOWClassAttributeDescription & AttributeDescription> = new CaseInsensitiveMap();

    public static createOWClassDescription(data: IBaseOWClassDescription): Promise<OWClassDescription> {
        const result = new OWClassDescription();
        result._className = data.className;
        result._childClasses = data.childClasses;
        result._relatedClasses = data.relatedClasses;
        result._originalClassName = data.originalClassName;
        result._isPersistent = data.isPersistent;
        result._signingAllowed = data.signingAllowed;
        result._encryptingAllowed = data.encryptingAllowed;
        result._versioningAllowed = data.versioningAllowed;
        result._passwordMasterLink = data.passwordMasterLink;
        result._supportsContext = data.supportsContext;
        result._supportsHierarchy = data.supportsHierarchy;

        const promises = new Array<Promise<any>>();

        for (const attr of data.attributes) {
            promises.push(OWClassDescription._createAttrInfo(attr).then(desc => result._attributes.set(attr.attributeName, desc)));
        }

        return (Promise.all(promises)).then(() => result);
    }

    private static async _createAttrInfo(attr: OWAttributeDesciption): Promise<AttributeDescription> {
        const ctdp = SerializeFuncs.getTypeDescription(attr.type, attr.attributeName, (attr as IOWEnumAttributeDescription).enumName);

        const attrInfo: AttributeDescription = {
            ...attr,
            basicType: ctdp.basicType,
            defaultValue: ctdp.defaultValue,
        };

        return attrInfo;
    }

    public async getAttributeInfo(attributePath: string): Promise<AttributeDescription> {
        const releaser = await this._associationLoadingLocks.getLock(attributePath).lock();

        const separatorPosition = attributePath.indexOf(".");
        const attributeName = ObjectUtils.removeTypeCasting(separatorPosition > -1 ? attributePath.substring(0, separatorPosition) : attributePath);

        try {
            if (separatorPosition !== -1) {
                if (this._bindingAttributes.has(attributePath))
                    return this._bindingAttributes.get(attributePath);

                const remainingAttributePath = attributePath.substring(separatorPosition + 1);

                // Pokud "attributePath" obsahuje "|", pak má dojít k přetypování např. z "A|B.x -> B.x" a potom se zjišťují informace o třídě vpravo od "|",
                // jinak se vyhledají na aktuální třídě podle "attributeName"
                let className;
                if (attributePath.includes("|")) {
                    const retypedAttributePath = attributePath.split("|")[1];
                    const retypedSeparatorPosition = retypedAttributePath.indexOf(".");
                    className = retypedAttributePath.substring(0, retypedSeparatorPosition);
                } else {
                    modelLogger.warn(`Class ${this.className} doesn't contain binding attribute ${attributePath}!`); //`
                    if (!this._attributes.get(attributeName)) return null;
                    className = (this._attributes.get(attributeName) as IOWClassAttributeDescription).className;
                }

                const classMetadata = await ModelDescriptionsManager.instance.getClassInfo(className);
                const attributeMetadata = await classMetadata.getAttributeInfo(remainingAttributePath);

                this._bindingAttributes.set(attributePath, attributeMetadata);

                return attributeMetadata;
            } else {
                const attributeMetadata = this._attributes.get(attributeName);

                if (attributeMetadata != null)
                    return attributeMetadata;
                else {
                    modelLogger.warn(`Class ${this.className} doesn't contain attribute ${attributeName}!`); //`
                    //throw new EOWException(`Class ${this.className} doesn't contain attribute ${attributeName}!`); //`
                    return null;
                }
            }
        } finally {
            releaser.release();
        }
    }
}

//--------------------------------------------------------------------------------------------------------------------------------
// tridy ve kterych se data prenasi

// ReSharper disable InconsistentNaming

export interface IClassInsertRightResult {
    InsertRightValue: boolean;
    InsertRoleIDs: string[];
}

// ReSharper restore InconsistentNaming
